import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type ManageAgreementSectionId = 'cancellations';

export interface ManageAgreementSection {
    id: ManageAgreementSectionId;
    label: string;
    description: string;
}

interface Props {
    sections: ManageAgreementSection[];
    selected: ManageAgreementSectionId;
    onSelect: (id: ManageAgreementSectionId) => void;
    children: ReactNode;
}

const SECTION_BASE =
    'w-full rounded-lg border px-4 py-4 text-left transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mp-link';
const SECTION_SELECTED = 'border-mp-primary bg-mp-primary text-mp-primary-foreground';
const SECTION_IDLE = 'border-mp-border bg-mp-surface text-mp-foreground hover:bg-mp-background';

export function ManageAgreementTab({ sections, selected, onSelect, children }: Props) {
    const idPrefix = useId();
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const selectedIndex = Math.max(0, sections.findIndex((section) => section.id === selected));
    const tabId = (id: ManageAgreementSectionId) => `${idPrefix}-tab-${id}`;
    const panelId = `${idPrefix}-panel`;

    const moveTo = (index: number) => {
        const next = (index + sections.length) % sections.length;
        onSelect(sections[next].id);
        buttonRefs.current[next]?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const keys: Record<string, number> = {
            ArrowDown: selectedIndex + 1,
            ArrowUp: selectedIndex - 1,
            Home: 0,
            End: sections.length - 1,
        };
        if (!(event.key in keys)) return;
        event.preventDefault();
        moveTo(keys[event.key]);
    };

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
            <div
                role="tablist"
                aria-orientation="vertical"
                aria-label="Manage agreement"
                className="flex flex-col gap-3"
                onKeyDown={handleKeyDown}
            >
                {sections.map((section, index) => {
                    const isSelected = index === selectedIndex;
                    return (
                        <button
                            key={section.id}
                            ref={(element) => {
                                buttonRefs.current[index] = element;
                            }}
                            id={tabId(section.id)}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            aria-controls={panelId}
                            tabIndex={isSelected ? 0 : -1}
                            onClick={() => onSelect(section.id)}
                            className={twMerge(SECTION_BASE, isSelected ? SECTION_SELECTED : SECTION_IDLE)}
                        >
                            <span className="block text-base font-semibold">{section.label}</span>
                            <span
                                className={twMerge(
                                    'mt-1 block text-sm',
                                    isSelected ? 'text-mp-primary-foreground' : 'text-mp-muted',
                                )}
                            >
                                {section.description}
                            </span>
                        </button>
                    );
                })}
            </div>
            <div
                role="tabpanel"
                id={panelId}
                aria-labelledby={sections.length > 0 ? tabId(sections[selectedIndex].id) : undefined}
                className="min-w-0 space-y-4"
            >
                {children}
            </div>
        </div>
    );
}
